import type { Account, Workspace, ObjectDefinition, FieldDefinition, FieldType, GenericRecord } from "./types";

/* Cross-object report engine.
 *
 * Every reportable object is a `Source` that declares its own rows, dimensions,
 * and measures. A ReportDef names a source + measure + (optional) group-by
 * dimension + viz, and runReport() executes it. The global filter bar is
 * account-level (region/segment/health/owner); it cascades to OTHER objects by
 * joining back to the account through each source's `link` — so filtering to
 * "NA" narrows signals and tasks too, not just accounts. Config-type objects
 * (playbooks, integrations) have no account link and are left unfiltered. */

// validated categorical palette (scripts/validate_palette.js) + reserved status
export const CATEGORICAL = ["#C9821A", "#2E6FBE", "#2E9060", "#8A4FA8", "#BB472C"];
export const OTHER_COLOR = "#9B9587";
export const STATUS = { good: "#3F8F63", watch: "#C08A2A", risk: "#BB472C", neutral: "#9B9587" } as const;

export type Agg = "count" | "sum" | "avg";
export type Viz = "metric" | "bar" | "donut" | "line" | "table";
export type Format = "usd" | "num" | "pct" | "health" | "text";

export interface DimField { id: string; label: string; get: (r: any, ws: Workspace) => string; colors?: Record<string, string>; }
export interface MeasureField { id: string; label: string; agg: Agg; format: Format; get?: (r: any) => number; }
export interface Source {
  id: string;
  label: string;
  icon: string;
  rows: (ws: Workspace) => any[];
  dims: DimField[];
  measures: MeasureField[];
  link?: (r: any, ws: Workspace) => Account | undefined; // join to account for global filters
  name: (r: any) => string;
  supportsLine?: boolean;
}

export interface Filter { field: string; op: "is"; value: string; }
export interface ReportDef {
  id: string;
  title: string;
  source: string;
  viz: Viz;
  measureId: string;
  dimensionId?: string;
  filters?: Filter[];
  limit?: number;
}

const ownerName = (ws: Workspace, id: string) => ws.users.find((u) => u.id === id)?.name ?? "Unassigned";
const bandLabel: Record<string, string> = { good: "Healthy", watch: "Watch", risk: "At risk" };

/** Account-level dimensions the global filter bar exposes (and how to read them). */
export const GLOBAL_DIMS: DimField[] = [
  { id: "region", label: "Region", get: (a: Account) => a.region },
  { id: "segment", label: "Segment", get: (a: Account) => String(a.attributes.segment ?? "—") },
  { id: "healthBand", label: "Health", get: (a: Account) => bandLabel[a.healthBand] },
  { id: "owner", label: "Owner", get: (a: Account, ws) => ownerName(ws, a.ownerId) },
];
const resolveAccount = (a: Account, field: string, ws: Workspace) =>
  GLOBAL_DIMS.find((d) => d.id === field)?.get(a, ws) ?? "";

/* ------------------------------- sources ------------------------------- */

export const SOURCES: Source[] = [
  {
    id: "accounts", label: "Accounts", icon: "building", supportsLine: true,
    rows: (ws) => ws.accounts,
    name: (a: Account) => a.name,
    link: (a: Account) => a,
    dims: [
      { id: "region", label: "Region", get: (a: Account) => a.region },
      { id: "segment", label: "Segment", get: (a: Account) => String(a.attributes.segment ?? "—") },
      { id: "lifecycleStage", label: "Lifecycle stage", get: (a: Account) => a.lifecycleStage },
      { id: "healthBand", label: "Health band", get: (a: Account) => bandLabel[a.healthBand], colors: { Healthy: STATUS.good, Watch: STATUS.watch, "At risk": STATUS.risk } },
      { id: "owner", label: "Owner", get: (a: Account, ws) => ownerName(ws, a.ownerId) },
      { id: "industry", label: "Industry", get: (a: Account) => a.industry },
    ],
    measures: [
      { id: "count", label: "Accounts", agg: "count", format: "num" },
      { id: "arr", label: "ARR", agg: "sum", format: "usd", get: (a: Account) => a.arr },
      { id: "avgHealth", label: "Avg health", agg: "avg", format: "health", get: (a: Account) => a.health },
      { id: "seats", label: "Seats", agg: "sum", format: "num", get: (a: Account) => a.seats },
      { id: "expansion", label: "Avg expansion potential", agg: "avg", format: "pct", get: (a: Account) => a.expansionPotential },
    ],
  },
  {
    id: "signals", label: "Signals", icon: "pulse",
    rows: (ws) => ws.signals,
    name: (s) => s.title,
    link: (s, ws) => ws.accounts.find((a) => a.id === s.accountId),
    dims: [
      { id: "band", label: "Band", get: (s) => s.band, colors: { risk: STATUS.risk, watch: STATUS.watch, positive: STATUS.good } },
      { id: "source", label: "Source", get: (s) => s.source },
      { id: "status", label: "Status", get: (s) => s.status, colors: { open: STATUS.watch, acknowledged: STATUS.neutral, resolved: STATUS.good } },
      { id: "account", label: "Account", get: (s) => s.accountName },
    ],
    measures: [
      { id: "count", label: "Signals", agg: "count", format: "num" },
      { id: "impact", label: "Total health impact", agg: "sum", format: "num", get: (s) => s.impact },
    ],
  },
  {
    id: "tasks", label: "Tasks", icon: "check",
    rows: (ws) => ws.tasks,
    name: (t) => t.title,
    link: (t, ws) => ws.accounts.find((a) => a.id === t.accountId),
    dims: [
      { id: "status", label: "Status", get: (t) => t.status, colors: { todo: STATUS.neutral, doing: STATUS.watch, done: STATUS.good } },
      { id: "priority", label: "Priority", get: (t) => t.priority, colors: { high: STATUS.risk, medium: STATUS.watch, low: STATUS.neutral } },
      { id: "source", label: "Source", get: (t) => t.source },
      { id: "owner", label: "Owner", get: (t, ws) => ownerName(ws, t.ownerId) },
    ],
    measures: [{ id: "count", label: "Tasks", agg: "count", format: "num" }],
  },
  {
    id: "playbooks", label: "Playbooks", icon: "route",
    rows: (ws) => ws.playbooks,
    name: (p) => p.name,
    dims: [
      { id: "category", label: "Category", get: (p) => p.category },
      { id: "enabled", label: "State", get: (p) => (p.enabled ? "Enabled" : "Disabled"), colors: { Enabled: STATUS.good, Disabled: STATUS.neutral } },
    ],
    measures: [
      { id: "count", label: "Playbooks", agg: "count", format: "num" },
      { id: "activeRuns", label: "Runs in flight", agg: "sum", format: "num", get: (p) => p.activeRuns },
      { id: "completedRuns", label: "Completed runs", agg: "sum", format: "num", get: (p) => p.completedRuns },
      { id: "successRate", label: "Avg success rate", agg: "avg", format: "pct", get: (p) => p.successRate },
    ],
  },
  {
    id: "integrations", label: "Integrations", icon: "plug",
    rows: (ws) => ws.integrations,
    name: (i) => i.name,
    dims: [
      { id: "category", label: "Category", get: (i) => i.category },
      { id: "status", label: "Status", get: (i) => i.status, colors: { connected: STATUS.good, available: STATUS.neutral } },
      { id: "dataType", label: "Data type", get: (i) => i.dataType },
      { id: "direction", label: "Direction", get: (i) => i.direction },
    ],
    measures: [
      { id: "count", label: "Integrations", agg: "count", format: "num" },
      { id: "recordsSynced", label: "Records synced", agg: "sum", format: "num", get: (i) => i.recordsSynced ?? 0 },
    ],
  },
];

/* ---- custom / flexible objects become sources automatically ----
 * A custom object's field types tell us what's a dimension (select, boolean,
 * reference, text) and what's a measure (number, currency, percent). So any
 * object with rows — including ones a user defines in Data Studio — is
 * reportable with no extra configuration. */

const fmtForField = (t: FieldType): Format => (t === "currency" ? "usd" : t === "percent" ? "pct" : "num");
const DIM_TYPES: FieldType[] = ["select", "boolean", "reference", "text"];
const MEASURE_TYPES: FieldType[] = ["number", "currency", "percent"];

function statusColorsFor(f: FieldDefinition): Record<string, string> | undefined {
  if (f.key.toLowerCase().includes("sentiment")) return { positive: STATUS.good, neutral: STATUS.neutral, negative: STATUS.risk };
  return undefined;
}

function objectSource(obj: ObjectDefinition, ws: Workspace): Source | null {
  const rows = ws.records?.[obj.apiName];
  if (!rows || rows.length === 0) return null;

  const dims: DimField[] = obj.fields.filter((f) => DIM_TYPES.includes(f.type)).map((f) => ({
    id: f.key,
    label: f.label,
    colors: statusColorsFor(f),
    get: (r: GenericRecord, ws2: Workspace) => {
      const v = r.attributes[f.key];
      if (f.type === "boolean") return v ? "Yes" : "No";
      if (f.type === "reference" && f.refObject === "account") return ws2.accounts.find((a) => a.id === v)?.name ?? String(v);
      return v == null || v === "" ? "—" : String(v);
    },
  }));

  const measures: MeasureField[] = [
    { id: "count", label: obj.labelPlural, agg: "count", format: "num" },
    ...obj.fields.filter((f) => MEASURE_TYPES.includes(f.type)).flatMap((f) => [
      { id: `${f.key}__sum`, label: `Total ${f.label}`, agg: "sum" as Agg, format: fmtForField(f.type), get: (r: GenericRecord) => Number(r.attributes[f.key]) || 0 },
      { id: `${f.key}__avg`, label: `Avg ${f.label}`, agg: "avg" as Agg, format: fmtForField(f.type), get: (r: GenericRecord) => Number(r.attributes[f.key]) || 0 },
    ]),
  ];

  const accountRef = obj.fields.find((f) => f.type === "reference" && f.refObject === "account");
  const nameField = obj.fields.find((f) => f.type === "text");

  return {
    id: obj.apiName,
    label: obj.label,
    icon: obj.icon,
    rows: (ws2) => ws2.records?.[obj.apiName] ?? [],
    dims,
    measures,
    link: accountRef ? (r: GenericRecord, ws2: Workspace) => ws2.accounts.find((a) => a.id === r.attributes[accountRef.key]) : undefined,
    name: (r: GenericRecord) => (nameField ? String(r.attributes[nameField.key]) : r.id),
  };
}

function dynamicSources(ws: Workspace): Source[] {
  const curated = new Set(SOURCES.map((s) => s.id));
  return ws.objects
    .filter((o) => o.apiName !== "account" && !curated.has(o.apiName))
    .map((o) => objectSource(o, ws))
    .filter((s): s is Source => s !== null);
}

/** Curated sources + every custom object that has rows. */
export function sourcesFor(ws: Workspace): Source[] {
  return [...SOURCES, ...dynamicSources(ws)];
}

export const getSource = (ws: Workspace, id: string): Source => sourcesFor(ws).find((s) => s.id === id) ?? SOURCES[0];

/* ------------------------------- engine ------------------------------- */

function aggregate(rows: any[], m: MeasureField): number {
  if (m.agg === "count" || !m.get) return rows.length;
  const vals = rows.map(m.get);
  const sum = vals.reduce((s, v) => s + v, 0);
  return m.agg === "sum" ? sum : rows.length ? Math.round(sum / rows.length) : 0;
}

function passesGlobal(row: any, src: Source, ws: Workspace, gfs: Filter[]): boolean {
  if (!src.link || gfs.length === 0) return true;
  const acct = src.link(row, ws);
  if (!acct) return true;
  return gfs.every((f) => resolveAccount(acct, f.field, ws) === f.value);
}

function passesLocal(row: any, src: Source, ws: Workspace, fs: Filter[]): boolean {
  return fs.every((f) => {
    const dim = src.dims.find((d) => d.id === f.field);
    return dim ? dim.get(row, ws) === f.value : true;
  });
}

export function colorFor(src: Source, dim: DimField, value: string, ws: Workspace): string {
  if (dim.colors?.[value]) return dim.colors[value];
  const domain = [...new Set(src.rows(ws).map((r) => dim.get(r, ws)))].sort();
  const idx = domain.indexOf(value);
  return idx >= 0 && idx < CATEGORICAL.length ? CATEGORICAL[idx] : OTHER_COLOR;
}

export type ReportResult =
  | { kind: "metric"; value: number; format: Format }
  | { kind: "series"; series: { key: string; value: number; color: string }[]; format: Format }
  | { kind: "line"; points: number[]; labels: string[]; format: Format }
  | { kind: "table"; columns: string[]; rows: { name: string; cells: string[] }[] };

export function runReport(ws: Workspace, def: ReportDef, globalFilters: Filter[]): ReportResult {
  const src = getSource(ws, def.source);
  const measure = src.measures.find((m) => m.id === def.measureId) ?? src.measures[0];
  const rows = src.rows(ws).filter((r) => passesGlobal(r, src, ws, globalFilters) && passesLocal(r, src, ws, def.filters ?? []));

  if (def.viz === "metric") return { kind: "metric", value: aggregate(rows, measure), format: measure.format };

  if (def.viz === "line") {
    // accounts-only: average each account's 12-week health series
    const pts: number[] = [];
    for (let i = 0; i < 12; i++) {
      const vals = rows.map((r: Account) => r.healthTrend?.[i]).filter((v): v is number => v != null);
      pts.push(vals.length ? Math.round(vals.reduce((s, v) => s + v, 0) / vals.length) : 0);
    }
    return { kind: "line", points: pts, labels: pts.map((_, i) => (i === 0 ? "12w" : i === 11 ? "now" : "")), format: "health" };
  }

  if (def.viz === "table") {
    const m = measure.get ? measure : src.measures.find((x) => x.get) ?? measure;
    const sorted = [...rows].sort((a, b) => (m.get ? m.get(b) - m.get(a) : 0));
    return {
      kind: "table",
      columns: ["Name", m.label],
      rows: sorted.slice(0, def.limit ?? 6).map((r) => ({ name: src.name(r), cells: [fmt(m.get ? m.get(r) : 1, m.format)] })),
    };
  }

  // bar / donut → group by dimension
  const dim = src.dims.find((d) => d.id === def.dimensionId) ?? src.dims[0];
  const groups = new Map<string, any[]>();
  for (const r of rows) {
    const key = dim.get(r, ws);
    (groups.get(key) ?? groups.set(key, []).get(key)!).push(r);
  }
  let series = [...groups.entries()].map(([key, gr]) => ({ key, value: aggregate(gr, measure), color: colorFor(src, dim, key, ws) }));
  series.sort((a, b) => b.value - a.value);
  if (series.length > 5 && def.viz === "bar") {
    const head = series.slice(0, 4);
    head.push({ key: "Other", value: series.slice(4).reduce((s, g) => s + g.value, 0), color: OTHER_COLOR });
    series = head;
  }
  return { kind: "series", series, format: measure.format };
}

export function fmt(v: number, f: Format): string {
  if (f === "usd") {
    if (Math.abs(v) >= 1e6) return `$${(v / 1e6).toFixed(2)}M`;
    if (Math.abs(v) >= 1e3) return `$${Math.round(v / 1e3)}K`;
    return `$${v.toLocaleString()}`;
  }
  if (f === "pct" || f === "health") return `${v}${f === "pct" ? "%" : ""}`;
  return v.toLocaleString();
}
